import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { favoritesApi } from '../services/api';
import { extractApiError } from '../utils/errorUtils';
import { showError } from '../utils/toast';

interface FavoriteButtonProps {
  fairId: number;
  onFavoriteChange?: (isFavorited: boolean) => void;
  className?: string;
}

export default function FavoriteButton({ fairId, onFavoriteChange, className = '' }: FavoriteButtonProps) {
  const [isFavorited, setIsFavorited] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuthAndFavoriteStatus = React.useCallback(async () => {
    // Check if user is authenticated by checking for access token
    const token = localStorage.getItem('access_token');
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    setIsAuthenticated(true);

    try {
      const favorites = await favoritesApi.getFavorites();
      // Defensive programming: handle case where favorites might be undefined (e.g., during 401 handling)
      const favorited = favorites?.some(fav => fav.fair_id === fairId) ?? false;
      setIsFavorited(favorited);
    } catch (error: unknown) {
      // User might not be authenticated or token expired
      console.error('Failed to check favorite status:', error);
      setIsAuthenticated(false);
      setIsFavorited(false);
    }
  }, [fairId]);

  useEffect(() => {
    checkAuthAndFavoriteStatus();
  }, [checkAuthAndFavoriteStatus]);

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent navigation if button is inside a link
    e.stopPropagation();

    if (!isAuthenticated) {
      // Redirect to regular user login
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }

    setLoading(true);

    try {
      if (isFavorited) {
        await favoritesApi.removeFavorite(fairId);
        setIsFavorited(false);
        onFavoriteChange?.(false);
      } else {
        await favoritesApi.addFavorite(fairId);
        setIsFavorited(true);
        onFavoriteChange?.(true);
      }
    } catch (err: unknown) {
      const message = extractApiError(err).message;
      console.error('Failed to toggle favorite:', message);
      showError('Failed to update favorite', {
        description: message,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <button
        onClick={handleToggleFavorite}
        className={`p-2 rounded-full hover:bg-gray-100 transition-colors ${className}`}
        title="Login to favorite"
        disabled={loading}
      >
        <Heart className="h-5 w-5 text-gray-400" />
      </button>
    );
  }

  return (
    <button
      onClick={handleToggleFavorite}
      className={`p-2 rounded-full transition-all ${
        isFavorited
          ? 'bg-red-50 hover:bg-red-100'
          : 'hover:bg-gray-100'
      } ${loading ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      disabled={loading}
    >
      <Heart
        className={`h-5 w-5 transition-all ${
          isFavorited
            ? 'fill-red-500 text-red-500'
            : 'text-gray-600'
        }`}
      />
    </button>
  );
}
